import { h, Component } from 'preact';
import pure from 'utils/pure';
import { once, joinClasses } from 'utils/utils';
import s from 'components/shared/backgroundDrawer/backgroundDrawer.sss';


export default @pure class BackgroundDrawer extends Component {

	constructor() {
		super();
		this.naturalSize = { width: 0, height: 0 };
		this.loop = this.loop.bind(this);
	}

	componentDidMount() {
		if (!this.c) return;
		this.ctx = this.c.getContext('2d');
		this.changedBackground(this.props.background);
	}

	componentWillReceiveProps({ active, background, activePage }) {
		if (active !== this.props.active) {
			this[active ? 'play' : 'stop']();
		}
		// Check for activePage as well (sometimes styles are page-specific)
		if (background !== this.props.background) {
			this.changedBackground(background);
		} else if (activePage !== this.props.activePage) {
			this.backgroundWillChange = true;
		}
	}

	componentDidUpdate() {
		if (this.backgroundWillChange) {
			// Wait until the rendering is done
			// urgh
			setTimeout(() => this.changedBackground(this.props.background), 500);
			this.backgroundWillChange = false;
		}
	}

	changedBackground(bg) {
		this.stop();
		this.isInit = false;
		this.bgelem = this.bg = null;
		// Should also take repeat into account (and maybe color for when it's not cover)

		this.drawMode = { size: 'cover', pos: ['50%', '50%'] };
		if (bg && typeof bg === 'string') bg = document.getElementById(bg);
		if (!bg) return;
		this.bgelem = this.bg = bg;

		// Try to not break when HTMLMediaElement is not defined ...
		const media = window.HTMLMediaElement || function(){};
		if (bg instanceof media) { // Video element
			if (bg.readyState >= 2) {
				this.ready();
			} else {
				bg.addEventListener('canplay', once(this.ready.bind(this)));
			}
		} else if (bg instanceof Image) { // Image element
			if (bg.complete) {
				this.ready();
			} else {
				bg.addEventListener('load', once(this.ready.bind(this)));
			}
		} else if (bg instanceof HTMLElement) { // Element with background
			const style = getComputedStyle(bg, null);
			if (!style) {
				const bg = document.createElement('canvas');
				bg.width = bg.height = 1;
				this.bg = bg;
				this.ready();
				return;
			}

			const cssBg = style.backgroundImage;
			if (cssBg.indexOf('url(') === 0) {
				this.drawMode.size = style.backgroundSize;
				// getComputedStyle returns % instead of keywords like top/left
				this.drawMode.pos = style.backgroundPosition.split(' ');
				const quotes = +(cssBg[4] === '"' || cssBg[4] === '\'');
				const imgSrc = cssBg.slice(4 + quotes, -1 - quotes);

				this.bg = new Image();
				this.bg.onload = once(this.ready.bind(this));
				this.bg.onerror = once(() => {
					// color fallback
					const bg = document.createElement('canvas');
					const ctx = bg.getContext('2d');
					bg.width = bg.height = 1;
					ctx.fillStyle = style.backgroundColor;
					ctx.fillRect(0, 0, 1, 1);
					this.bg = bg;
					this.ready();
				});
				// this.bg.crossOrigin = 'anonymous';
				this.bg.src = imgSrc;
			} else {
				const bg = document.createElement('canvas');
				const ctx = bg.getContext('2d');
				bg.width = bg.height = 1;
				ctx.fillStyle = style.backgroundColor;
				ctx.fillRect(0, 0, 1, 1);
				this.bg = bg;
				this.ready();
			}
		}
	}

	ready() {
		if (this.isInit || !this.bg) return;
		this.isInit = true;

		const bg = this.bg;
		this.naturalSize = {
			width:  bg.videoWidth || bg.naturalWidth || bg.width || bg.offsetWidth,
			height: bg.videoHeight || bg.naturalHeight || bg.height || bg.offsetHeight
		};

		if (this.props.active) {
			this.play();
		}
	}

	loop() {
		if (!this.stopped) {
			this.timer = requestAnimationFrame(this.loop);
		}
		this.refresh();
	}

	stop() {
		if (this.stopped) return;
		this.stopped = true;
		cancelAnimationFrame(this.timer);
	}

	play() {
		if (!this.stopped) return;
		this.first = true;
		this.stopped = !this.props.continuous;
		this.timer = requestAnimationFrame(this.loop);
	}

	refreshCached() {
		if (this.props.preprocessing) {
			this.cached = this.props.preprocessing(this.bg, this.naturalSize);
		} else {
			this.cached = null;
		}
	}

	refreshSize() {
		const c = this.c;
		if (!c) return;

		const w = c.offsetWidth;
		const h = c.offsetHeight;

		if (c.width !== w) c.width = w;
		if (c.height !== h) c.height = h;
	}

	refresh() {
		const shldRfrsh = this.props.shouldRefresh;
		if (!this.first && shldRfrsh != undefined && (typeof shldRfrsh === 'function' ? !shldRfrsh.call(this) : !shldRfrsh)) {
			return;
		}

		if (this.first) this.refreshCached();
		this.first = false;

		this.refreshSize();

		if (!this.bg || !this.c) return;
		const c = this.c;
		const ctx = this.ctx;

		ctx.clearRect(0, 0, c.width, c.height);

		const bgRect = this.bgelem.getBoundingClientRect();
		const cRect = c.getBoundingClientRect();

		const bgIRect = {
			top:    bgRect.top,
			left:   bgRect.left,
			width:  bgRect.right - bgRect.left,
			height: bgRect.bottom - bgRect.top
		};
		const nSize = this.naturalSize;

		let inRatio;
		let extRatio;

		switch (this.drawMode.size) {
			case 'cover':
				inRatio = nSize.width / nSize.height;
				extRatio = bgIRect.width / bgIRect.height;
				if (inRatio > extRatio) {
					bgIRect.width = bgIRect.height * inRatio;
				} else {
					bgIRect.height = bgIRect.width / inRatio;
				}
				break;
			case 'contain':
				inRatio = nSize.width / nSize.height;
				extRatio = bgIRect.width / bgIRect.height;
				if (inRatio < extRatio) {
					bgIRect.width = bgIRect.height * inRatio;
				} else {
					bgIRect.height = bgIRect.width / inRatio;
				}
				break;
			default:
				// Support for other size values ? (%/px combinations)
		}

		const px = this.drawMode.pos[0];
		const pxv = parseFloat(px) || 0;
		if (px && px.slice(-1) === '%') {
			bgIRect.left = bgRect.left +  (pxv / 100) * (bgRect.right - bgRect.left - bgIRect.width);
		} else { // Assume pixels (but should check)
			bgIRect.left = bgRect.left + pxv;
		}

		const py = this.drawMode.pos[1];
		const pyv = parseFloat(py) || 0;
		if (py && py.slice(-1) === '%') {
			bgIRect.top = bgRect.top +  (pyv / 100) * (bgRect.bottom - bgRect.top - bgIRect.height);
		} else {
			bgIRect.top = bgRect.top + pyv;
		}

		let facW;
		let facH;
		if (this.cached) {
			facW = this.cached.width / bgIRect.width;
			facH = this.cached.height / bgIRect.height;
		} else {
			facW = this.naturalSize.width / bgIRect.width;
			facH = this.naturalSize.height / bgIRect.height;
		}

		const left = (cRect.left - bgIRect.left) * facW;
		const top = (cRect.top - bgIRect.top) * facH;

		const w = c.width * facW;
		const h = c.height * facH;

		try {
			ctx.drawImage(this.cached || this.bg, left, top, w, h, 0, 0, c.width, c.height);
		} catch (e) {
			// Error when image is broken
		}
	}

	render(props) {
		const {
			active
		} = props;

		return (
			<canvas class={joinClasses(s.canvas, !active && s.hidden, this.props.class)} ref={$ref => $ref && (this.c = $ref)} />
		);
	}

}
